import { effect, Injector, signal } from '@angular/core';
import type { Category } from '@app/domain/entities';
import type { ISettingsRepository } from '@app/domain/ports';
import { MonobankAccountMapping, MonobankImportConfig } from './monobank-settings.models';
import { MONOBANK_SETTINGS } from './monobank-setting-keys';

export class MonobankSettingsService {
  readonly monobankEnabled = signal(false);

  constructor(
    private readonly settings: ISettingsRepository,
    injector: Injector,
  ) {
    this.monobankEnabled.set(this.settings.get<boolean>(MONOBANK_SETTINGS.IMPORT_ENABLED, false));

    effect(
      () => {
        this.settings.set(MONOBANK_SETTINGS.IMPORT_ENABLED, this.monobankEnabled());
        if (!this.monobankEnabled()) {
          void this.settings.removeAsync(MONOBANK_SETTINGS.API_TOKEN);
        }
      },
      { injector },
    );
  }

  enableMonobank(): void {
    this.monobankEnabled.set(true);
  }

  disableMonobank(): void {
    this.monobankEnabled.set(false);
  }

  getApiToken(): Promise<string | undefined> {
    return this.settings.getAsync<string>(MONOBANK_SETTINGS.API_TOKEN);
  }

  saveApiToken(token: string): Promise<void> {
    return this.settings.setAsync(MONOBANK_SETTINGS.API_TOKEN, token);
  }

  getAccountMapping(): Record<string, MonobankAccountMapping> | undefined {
    return this.settings.get<Record<string, MonobankAccountMapping>>(MONOBANK_SETTINGS.ACCOUNT_MAPPING);
  }

  saveAccountMapping(mapping: Record<string, MonobankAccountMapping>): void {
    this.settings.set(MONOBANK_SETTINGS.ACCOUNT_MAPPING, mapping);
  }

  getImportConfig(): MonobankImportConfig | undefined {
    return this.settings.get<MonobankImportConfig>(MONOBANK_SETTINGS.IMPORT_CONFIG);
  }

  saveImportConfig(config: MonobankImportConfig): void {
    this.settings.set(MONOBANK_SETTINGS.IMPORT_CONFIG, config);
  }

  normalizeImportConfig(
    config: MonobankImportConfig | undefined,
    categories: readonly Category[],
  ): MonobankImportConfig | undefined {
    if (!config) {
      return undefined;
    }

    const visibleCategories = new Map(
      categories.filter((category) => !category.hidden).map((category) => [category.id, category] as const),
    );

    const normalized: MonobankImportConfig = {};

    if (config.expenseCategoryId !== undefined && visibleCategories.has(config.expenseCategoryId)) {
      normalized.expenseCategoryId = config.expenseCategoryId;
    }

    if (
      normalized.expenseCategoryId !== undefined &&
      this.isValidChildCategory(normalized.expenseCategoryId, config.expenseSubcategoryId, visibleCategories)
    ) {
      normalized.expenseSubcategoryId = config.expenseSubcategoryId;
    }

    if (config.incomeCategoryId !== undefined && visibleCategories.has(config.incomeCategoryId)) {
      normalized.incomeCategoryId = config.incomeCategoryId;
    }

    if (
      normalized.incomeCategoryId !== undefined &&
      this.isValidChildCategory(normalized.incomeCategoryId, config.incomeSubcategoryId, visibleCategories)
    ) {
      normalized.incomeSubcategoryId = config.incomeSubcategoryId;
    }

    if (typeof config.allowReimportRecords === 'boolean') {
      normalized.allowReimportRecords = config.allowReimportRecords;
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private isValidChildCategory(
    parentId: number | undefined,
    childId: number | undefined,
    categories: ReadonlyMap<number, Category>,
  ): boolean {
    if (parentId === undefined || childId === undefined) {
      return false;
    }

    const child = categories.get(childId);
    return child?.parentId === parentId;
  }
}
